import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { GradientTextScroll } from "./TextEffects";

const stepKeys = ["step_1", "step_2", "step_3"] as const;
const stepEmojis = ["📝", "✨", "💬"];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.6, type: "spring" as const, stiffness: 200, damping: 20 },
  },
};

export const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="py-16 sm:py-20 px-4 relative" id="how-it-works">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            {t('how_it_works.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            {t('how_it_works.section_title_prefix')} <GradientTextScroll>{t('how_it_works.section_title_highlight')}</GradientTextScroll>
          </h2>
        </ScrollReveal>

        <motion.div
          className="grid md:grid-cols-3 gap-10 max-w-4xl mx-auto relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={containerVariants}
        >
          <div
            className="hidden md:block absolute top-[56px] left-[16%] right-[16%] h-[2px]"
            style={{ background: "linear-gradient(90deg, hsl(var(--primary) / 0.2), hsl(var(--primary) / 0.5), hsl(var(--primary) / 0.2))" }}
          />

          {stepKeys.map((key, i) => (
            <motion.div key={key} variants={itemVariants} className="text-center relative">
              <motion.div
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/20 text-4xl flex items-center justify-center mx-auto mb-6 shadow-warm-md relative z-10"
                whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {stepEmojis[i]}
              </motion.div>
              <div className="inline-block px-3 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-bold mb-3">
                {t('how_it_works.step_label')} {i + 1}
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">{t(`how_it_works.${key}.title`)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-[260px] mx-auto">{t(`how_it_works.${key}.description`)}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
